import React, { useState, useEffect } from "react";
import { Menu, X, Instagram, Linkedin, Download } from "lucide-react";
import { LOGO_URL } from "../../lib/assets";

const NAV = [
    { href: "#como-funciona", label: "Como funciona" },
    { href: "#dentria", label: "DentrIA" },
    { href: "#meu-filtro", label: "Meu Filtro" },
    { href: "#minha-meta", label: "Minha Meta" },
    { href: "#beneficios", label: "Benefícios" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/85 backdrop-blur-xl border-b border-slate-100 shadow-[0_1px_0_rgba(11,28,58,0.04)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 lg:h-20">
                    <a
                        href="#top"
                        data-testid="logo-link"
                        className="flex items-center gap-2.5"
                    >
                        <img
                            src={LOGO_URL}
                            alt="Dentro"
                            className="h-9 w-9 rounded-lg object-cover"
                        />
                        <span className="font-display text-xl font-extrabold text-[#0B1C3A] tracking-tight">
                            Dentro
                        </span>
                    </a>

                    <nav className="hidden lg:flex items-center gap-1">
                        {NAV.map((n) => (
                            <a
                                key={n.href}
                                href={n.href}
                                data-testid={`nav-${n.href.replace("#", "")}`}
                                className="px-4 py-2 text-sm font-semibold text-[#0B1C3A]/75 hover:text-[#16A34A] transition-colors rounded-full"
                            >
                                {n.label}
                            </a>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        <a
                            href="https://instagram.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-instagram"
                            aria-label="Instagram"
                            className="h-10 w-10 grid place-items-center rounded-full text-[#0B1C3A]/70 hover:text-[#16A34A] hover:bg-slate-100 transition-colors"
                        >
                            <Instagram size={18} />
                        </a>
                        <a
                            href="https://linkedin.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-linkedin"
                            aria-label="LinkedIn"
                            className="h-10 w-10 grid place-items-center rounded-full text-[#0B1C3A]/70 hover:text-[#16A34A] hover:bg-slate-100 transition-colors"
                        >
                            <Linkedin size={18} />
                        </a>
                        <a
                            href="#download"
                            data-testid="nav-download-cta"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#16A34A] text-white text-sm font-bold shadow-[0_10px_24px_-10px_rgba(22,163,74,0.6)] hover:bg-[#15803d] hover:scale-[1.02] transition-all"
                        >
                            <Download size={16} />
                            Baixar app
                        </a>
                    </div>

                    <button
                        className="lg:hidden h-10 w-10 grid place-items-center rounded-full text-[#0B1C3A] hover:bg-slate-100"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Abrir menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-white border-t border-slate-100 shadow-lg"
                >
                    <div className="px-6 py-4 flex flex-col gap-1">
                        {NAV.map((n) => (
                            <a
                                key={n.href}
                                href={n.href}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.href.replace("#", "")}`}
                                className="py-3 text-base font-semibold text-[#0B1C3A] hover:text-[#16A34A]"
                            >
                                {n.label}
                            </a>
                        ))}
                        <div className="flex items-center gap-3 pt-2 mt-2 border-t border-slate-100">
                            <a
                                href="https://instagram.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="mobile-nav-instagram"
                                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold text-[#0B1C3A]"
                            >
                                <Instagram size={16} /> Instagram
                            </a>
                            <a
                                href="https://linkedin.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="mobile-nav-linkedin"
                                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold text-[#0B1C3A]"
                            >
                                <Linkedin size={16} /> LinkedIn
                            </a>
                        </div>
                        <a
                            href="#download"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-nav-download-cta"
                            className="mt-3 text-center px-5 py-3 rounded-full bg-[#16A34A] text-white text-base font-bold"
                        >
                            Baixar app
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
